import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PointElement,
  Title,
  ChartOptions,
  GridLineOptions,
} from "chart.js";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  PointElement,
  Title
);

interface LineChartProps {
  title?: string;
  labels: string[];
  dataSets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor?: string;
  }[];
  xLabel?: string;
  yLabel?: string;
}

const LineChart: React.FC<LineChartProps> = ({ title, labels, dataSets, xLabel, yLabel }) => {
  const data = {
    labels,
    datasets: dataSets.map((set) => ({
      label: set.label,
      data: set.data,
      borderColor: set.borderColor,
      backgroundColor: set.backgroundColor || set.borderColor,
      borderWidth: 2,
      fill: false,
      pointBackgroundColor: set.borderColor,
      pointBorderColor: set.borderColor,
      pointRadius: 5,
      pointHoverRadius: 6,
      pointStyle: "circle",
      tension: 0.3, // Smooth curve for gradual incline
    })),
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const value = context.raw as number;
            return `${context.dataset.label}: ${value.toLocaleString()}`; // Handle currency or number without assuming percentage
          },
        },
      },
      title: {
        display: !!title,
        text: title,
        font: { size: 16 },
        align : "start",
        padding : 30
      },
    },
    scales: {
      x: {
        title: {
          display: !!xLabel,
          text: xLabel,
          font: { size: 14 },
        },
      },
      y: {
        title: {
          display: !!yLabel,
          text: yLabel,
          font: { size: 14 },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
          borderDash: [5, 5], 
          lineWidth: 1, 
          drawOnChartArea: true,
          display: true, 
          drawTicks: true, 
        } as unknown as GridLineOptions, 
        ticks: {
          // maxTicksLimit: 5, // Reduces the number of Y-axis ticks
          // stepSize: 5000, // Adjust this value based on the dataset range
          callback: (value) => `${value.toLocaleString()}`, 
        },
      },
    },
  };

  return (
    <div className="mt-1 px-5">
      <Line data={data} options={options} />
    </div>
  );
};

export default LineChart;
